
import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import IconButton from 'components/partials/button/icon-button';
import Button from 'components/partials/button/button';

import CeebCodeChip from './ceeb-code-chip';
import { useCeebCodesInput } from '../hooks/use-ceeb-codes-input';
import type { CeebCodesUpdater } from '../hooks/use-ceeb-codes-input';

import {
  CEEB_CODES_HELPER_TEXT,
  CEEB_CODES_LABEL,
  CEEB_REMOVE_ALL_UNMATCHED_LABEL,
} from '../calculations-step.constants';
import { getCeebUnmatchedMessage } from '../helpers/ceeb-codes';
import type { CeebCodeChip as CeebCodeChipModel } from '../calculations-step.types';

import Icons from 'assets/icons';

export interface CeebCodesFieldProps {
  labelFor: string;
  codes: CeebCodeChipModel[];
  onCodesChange: (updater: CeebCodesUpdater) => void;
  'data-testid'?: string;
}

const CeebCodesField = ({
  labelFor,
  codes,
  onCodesChange,
  'data-testid': dataTestId = labelFor,
}: CeebCodesFieldProps) => {
  const {
    draftText,
    handleChange,
    handlePaste,
    handleKeyDown,
    handleBlur,
    removeCode,
    removeAllUnmatched,
    clearAll,
    hasPending,
    matchedCount,
    unmatchedCount,
    totalCount,
  } = useCeebCodesInput({ codes, onCodesChange });

  const unmatchedMessage = hasPending
    ? null
    : getCeebUnmatchedMessage(matchedCount, totalCount);
  const showRemoveAllUnmatched =
    !hasPending && unmatchedCount > 0 && matchedCount > 0;
  // FR-24: zero matched (once resolved, with at least one code entered) is the
  // "failed" state — flag it on the field border, not just the chip colors.
  const isAllUnmatched = !hasPending && totalCount > 0 && matchedCount === 0;

  return (
    <Box sx={{ width: '100%' }}>
      <label htmlFor={labelFor} className="mb-1 flex font-semibold">
        {CEEB_CODES_LABEL}
        <Icons.RequiredIndicator
          customClass="w-[7px] h-[7px] ml-1 mt-1"
          data-testid={`${dataTestId}-required-indicator`}
        />
      </label>
      <span className="mb-1 block text-sm text-gray-600">
        {CEEB_CODES_HELPER_TEXT}
      </span>

      <Box sx={{ position: 'relative' }}>
        <Stack
          direction="row"
          sx={[
            {
              maxHeight: '120px',
              width: '100%',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              gap: 0.75,
              overflowY: 'auto',
              borderRadius: '0.375rem',
              borderWidth: '1px',
              p: 1,
              pr: 4,
            },
            isAllUnmatched && {
              borderColor: 'tokens.critical.600',
              '&:hover': { borderColor: 'tokens.critical.600' },
              '&:focus-within': { borderColor: 'tokens.critical.600' },
            },
            !isAllUnmatched && {
              borderColor: 'tokens.navy.200',
              '&:hover': { borderColor: 'tokens.navy.500' },
              '&:focus-within': { borderColor: 'tokens.navy.700' },
            },
          ]}
          data-testid={dataTestId}
        >
          {codes.map((chip) => (
            <CeebCodeChip key={chip.id} chip={chip} onRemove={removeCode} />
          ))}
          <textarea
            id={labelFor}
            className="flex-1 resize-none border-0 p-0 text-sm outline-none focus:ring-0"
            rows={3}
            value={draftText}
            onChange={(event) => handleChange(event.target.value)}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            data-testid={`${dataTestId}-input`}
          />
        </Stack>

        {!!codes.length && (
          <IconButton
            icon={<Icons.Close />}
            variant="reserve-text"
            size="xs"
            aria-label="Remove all CEEB codes"
            onClick={clearAll}
            className="absolute right-1 top-1"
            data-testid={`${dataTestId}-clear-all`}
          />
        )}
      </Box>

      {!!unmatchedMessage && (
        <Stack
          direction="row"
          sx={{
            mt: 1,
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Box
            component="span"
            sx={[
              {
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.875rem',
                lineHeight: '1.25rem',
                fontWeight: 500,
              },
              isAllUnmatched && { color: 'tokens.critical.600' },
              !isAllUnmatched && { color: 'tokens.amber.700' },
            ]}
            data-testid={`${dataTestId}-unmatched-message`}
          >
            <Icons.Warning customClass="size-4" />
            {unmatchedMessage}
          </Box>

          {showRemoveAllUnmatched && (
            <Button
              type="button"
              variant="reserve-outlined"
              size="xs"
              onClick={removeAllUnmatched}
              data-testid={`${dataTestId}-remove-all-unmatched`}
            >
              {CEEB_REMOVE_ALL_UNMATCHED_LABEL}
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default CeebCodesField;
